import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Copy, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * r66.8 — Duplicate Application Dialog
 * ====================================
 * Lets the user clone an existing application to a DIFFERENT university and
 * (optionally) reassign it to a new team / team member.
 *
 * Backend rules already enforced by POST /api/applications/{id}/duplicate:
 *   • Target university MUST differ from the source application's university.
 *   • An app for the same student at the target university blocks duplication
 *     (409 with the existing app number returned in the error message).
 *
 * Frontend behaviour:
 *   • Universities dropdown excludes the source application's university.
 *   • Teams dropdown is filtered to those that handle the chosen university
 *     (Team.university_ids contains the chosen uni). When no teams match, ALL
 *     teams are shown so the workflow isn't blocked.
 *   • Team Member dropdown is filtered by the chosen team (User.team_id).
 *   • All three pickers are optional except the university — the backend uses
 *     the caller as default `assigned_to`, but we strongly encourage picking.
 */
export const DuplicateApplicationDialog = ({
  open,
  onOpenChange,
  app,                 // source application object ({ id, university_id, ... })
  api,                 // authenticated axios-like helper from useAuth()
  onSuccess,           // (newApp) => void — caller refreshes its list / shows toast
}) => {
  const [universities, setUniversities] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [uniId, setUniId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [userId, setUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Reset form whenever the dialog opens for a fresh app
  useEffect(() => {
    if (!open) return;
    setUniId('');
    setTeamId('');
    setUserId('');
    setErrorMsg('');
  }, [open, app?.id]);

  // Fetch reference data on open
  useEffect(() => {
    if (!open || !api) return;
    (async () => {
      setLoadingMeta(true);
      try {
        const [uniRes, teamsRes, usersRes] = await Promise.all([
          api().get('/master-data?type=university'),
          api().get('/teams'),
          api().get('/users'),
        ]);
        const uniRaw = Array.isArray(uniRes.data) ? uniRes.data : (uniRes.data?.items || []);
        const teamsRaw = Array.isArray(teamsRes.data) ? teamsRes.data : (teamsRes.data?.items || []);
        const usersRaw = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.items || []);
        setUniversities(uniRaw);
        setTeams(teamsRaw);
        setUsers(usersRaw);
      } catch (e) {
        toast.error('Failed to load universities / teams.');
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, [open, api]);

  const targetUniversities = useMemo(
    () => universities.filter((u) => u.id !== (app?.university_id || '')),
    [universities, app?.university_id],
  );

  const eligibleTeams = useMemo(() => {
    if (!uniId) return teams;
    const filtered = teams.filter((t) =>
      Array.isArray(t.university_ids) && t.university_ids.includes(uniId)
    );
    return filtered.length ? filtered : teams; // fallback so flow isn't blocked
  }, [teams, uniId]);

  const eligibleUsers = useMemo(() => {
    if (!teamId) return users.filter((u) => u.is_active !== false);
    return users.filter((u) => u.team_id === teamId && u.is_active !== false);
  }, [users, teamId]);

  // When team changes, clear user if they no longer belong to it.
  useEffect(() => {
    if (!teamId || !userId) return;
    if (!eligibleUsers.some((u) => u.user_id === userId || u.id === userId)) {
      setUserId('');
    }
  }, [teamId, userId, eligibleUsers]);

  const handleSubmit = async () => {
    setErrorMsg('');
    if (!uniId) {
      setErrorMsg('Please pick a target university.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { university_id: uniId };
      if (teamId) payload.team_id = teamId;
      if (userId) payload.assigned_to = userId;
      const r = await api().post(`/applications/${app.id}/duplicate`, payload);
      const newApp = r.data;
      toast.success(
        `Duplicated to ${newApp.university_name || 'new university'} — App #${newApp.app_number || '…'}`,
      );
      onSuccess && onSuccess(newApp);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e?.message || 'Duplication failed');
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="duplicate-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="p-1.5 rounded-md bg-gradient-to-br from-amber-500 to-amber-500 text-white">
              <Copy className="w-4 h-4" />
            </span>
            Duplicate Application
          </DialogTitle>
          <DialogDescription>
            Cloning <strong>{app?.student_name || 'this applicant'}</strong>
            {app?.app_number ? <> (App #<strong>{app.app_number}</strong>)</> : null}
            {app?.university_name ? <> from <strong>{app.university_name}</strong></> : null}.
            <br />
            Pick a <strong>different university</strong> and (optionally) who'll handle it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ---------- Target university ---------- */}
          <div className="space-y-1.5">
            <Label htmlFor="dup-uni" className="text-sm font-semibold">
              Target University <span className="text-amber-500">*</span>
            </Label>
            <Select value={uniId} onValueChange={setUniId}>
              <SelectTrigger id="dup-uni" data-testid="duplicate-app-uni-select">
                <SelectValue placeholder={loadingMeta ? 'Loading universities…' : 'Pick a university'} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {targetUniversities.length === 0 && (
                  <div className="px-2 py-2 text-sm text-muted-foreground">
                    No other universities in master data.
                  </div>
                )}
                {targetUniversities.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`duplicate-app-uni-${u.id}`}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Cannot be the same university as the source application.
            </p>
          </div>

          {/* ---------- Team ---------- */}
          <div className="space-y-1.5">
            <Label htmlFor="dup-team" className="text-sm font-semibold">Team (optional)</Label>
            <Select value={teamId} onValueChange={setTeamId} disabled={!uniId}>
              <SelectTrigger id="dup-team" data-testid="duplicate-app-team-select">
                <SelectValue placeholder={uniId ? 'Pick a team that handles this university' : 'Pick a university first'} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {eligibleTeams.length === 0 && (
                  <div className="px-2 py-2 text-sm text-muted-foreground">
                    No teams configured.
                  </div>
                )}
                {eligibleTeams.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`duplicate-app-team-${t.id}`}>
                    {t.name}
                    {typeof t.member_count === 'number' && (
                      <span className="text-xs text-muted-foreground ml-2">· {t.member_count} members</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ---------- Team member ---------- */}
          <div className="space-y-1.5">
            <Label htmlFor="dup-user" className="text-sm font-semibold">Team Member (optional)</Label>
            <Select value={userId} onValueChange={setUserId} disabled={!uniId}>
              <SelectTrigger id="dup-user" data-testid="duplicate-app-user-select">
                <SelectValue placeholder={teamId ? 'Pick a team member' : 'Any team member (defaults to you)'} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {eligibleUsers.length === 0 && (
                  <div className="px-2 py-2 text-sm text-muted-foreground">
                    No team members available.
                  </div>
                )}
                {eligibleUsers.map((u) => {
                  const uid = u.user_id || u.id;
                  return (
                    <SelectItem key={uid} value={uid} data-testid={`duplicate-app-user-${uid}`}>
                      {u.full_name || u.name || u.email}
                      {u.team_name && (
                        <span className="text-xs text-muted-foreground ml-2">· {u.team_name}</span>
                      )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              If you leave this blank, the duplicate is assigned to you.
            </p>
          </div>

          {errorMsg && (
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-slate-800 text-sm"
              data-testid="duplicate-app-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="duplicate-app-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !uniId}
            className="bg-amber-500 hover:bg-slate-800"
            data-testid="duplicate-app-confirm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateApplicationDialog;
